// ismsRolesManager.util.ts

import { db, firebaseImport, FirebaseTimestamp } from '../../config/main_config'; // Adjust the path as needed
import { createToast } from './createToast.util'; // Utility for showing toast notifications
import { waitForCompanyID } from './waitForCompanyID.util';
import Tableordion from './tableordion.util';

/**
 * Interface representing an ISMS Role.
 */
interface ISMSRole {
    id: string;
    dateAdded: typeof FirebaseTimestamp;
    roleName: string;
    roleDescription: string;
}

/**
 * Fetches ISMS roles data from Firestore.
 * 
 * @returns A promise that resolves to an array of ISMSRole objects.
 */
export async function fetchISMSRoles(): Promise<ISMSRole[]> {
    const companyID = await waitForCompanyID(); // Dynamic retrieval of companyID
    const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = db.doc(docPath);

    try {
        const doc = await docRef.get();
        if (doc.exists) {
            const data = doc.data()?.ismsRoles;
            if (data && typeof data === 'object') {
                const ismsRoles: ISMSRole[] = Object.entries(data).map(([id, role]: [string, any]) => ({
                    id,
                    dateAdded: role.dateAdded instanceof FirebaseTimestamp ? role.dateAdded : 'N/A',
                    roleName: role.roleName,
                    roleDescription: role.roleDescription,
                }));
                return ismsRoles;
            } else {
                console.warn('No ISMS roles data found.');
                return [];
            }
        } else {
            console.warn('ISMS roles document does not exist.');
            return [];
        }
    } catch (error) {
        console.error('Error fetching ISMS roles:', error);
        createToast({
            message: 'Failed to load ISMS roles.',
            customStyles: {
                backgroundColor: '#ff0000',
                color: '#ffffff'
            }
        });
        return [];
    }
}

/**
 * Renders the ISMS roles into a tableordion.
 * 
 * @param ismsRoles - Array of ISMSRole objects.
 */
export async function renderISMSRolesTable(ismsRoles: ISMSRole[]): Promise<void> {
    const tableContainer = document.querySelector('#ismsRolesTable .tableordion-body');

    if (!tableContainer) {
        console.error('ISMS Roles table container not found.');
        return;
    }

    let html = ``;

    // Sort the ISMS roles by roleName
    ismsRoles.sort((a, b) => a.roleName.localeCompare(b.roleName, undefined, { numeric: true, sensitivity: 'base' }));

    ismsRoles.forEach(role => {
        html += `
            <tr data-id="${role.id}" class="tableordion-row">
                <td>${role.roleName}</td>
                <td>${role.roleDescription.replace(/\n/g, '<br>')}</td>
            </tr>
        `;
    });

    html += ``;

    tableContainer.innerHTML = html;

    const accordions = new Tableordion('.tableordion-row');
}

/**
 * Sets up a real-time listener for ISMS roles using onSnapshot.
 */
export function setupISMSRolesListener(): void {
    waitForCompanyID().then(companyID => {
        const docPath = `/customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
        const docRef = db.doc(docPath);

        docRef.onSnapshot(docSnapshot => {
            if (docSnapshot.exists) {
                const data = docSnapshot.data()?.ismsRoles;
                if (data && typeof data === 'object') {
                    const ismsRoles: ISMSRole[] = Object.entries(data).map(([id, role]: [string, any]) => ({
                        id,
                        dateAdded: role.dateAdded instanceof FirebaseTimestamp ? role.dateAdded : 'N/A',
                        roleName: role.roleName,
                        roleDescription: role.roleDescription,
                    }));
                    (window as any).ismsRoles = ismsRoles;
                    renderISMSRolesTable(ismsRoles);
                }
            }
        }, error => {
            console.error('Error with onSnapshot listener for ISMS roles:', error);
            createToast({
                message: 'Real-time updates for ISMS roles failed.',
                customStyles: {
                    backgroundColor: '#ff0000',
                    color: '#ffffff'
                }
            });
        });
    });
}

/**
 * Initializes the ISMS Roles table on page load.
 */
export async function initialiseISMSRoles(): Promise<void> {
    // Initial fetch and render of ISMS roles
    const ismsRoles = await fetchISMSRoles();
    (window as any).ismsRoles = ismsRoles; // Store globally for access if needed
    renderISMSRolesTable(ismsRoles);

    // Set up real-time listener for ISMS roles
    setupISMSRolesListener();
}